<template>
  <AuthLayout
    :title="layoutTitle"
    :subtitle="layoutSubtitle"
    @back="toMain"
  >
    <div v-if="!hasValidLink" class="auth-panel__form">
      <p class="auth-panel__invite-error">{{ $t('authInviteInvalidLink') }}</p>
      <button type="button" class="auth-panel__submit" @click="toMain">
        {{ $t('authBackHome') }}
      </button>
    </div>

    <form v-else class="auth-panel__form" @submit.prevent="register">
      <div v-if="inviteEmail" class="auth-panel__invite-email">
        <span class="auth-panel__invite-email-label">{{ $t('authInviteForEmail') }}</span>
        <span class="auth-panel__invite-email-value">{{ inviteEmail }}</span>
      </div>

      <AuthInput
        v-model="password"
        :label="$t('authPasswordLabel')"
        type="password"
        :placeholder="$t('pass')"
        :errors="passwordErrors"
      />

      <AuthInput
        v-model="confirmPassword"
        :label="$t('confirmPass')"
        type="password"
        :placeholder="$t('confirmPass')"
        :errors="confirmPasswordErrors"
      />

      <label class="auth-checkbox-row">
        <input v-model="agree" type="checkbox" />
        <span>
          {{ $t('agree') }}
          <a :href="DOCS?.doc_1" target="_blank" rel="noopener">{{ $t('conf2') }}</a>
          {{ $t('and') }}
          <a :href="DOCS?.doc_2" target="_blank" rel="noopener">{{ $t('conf3') }}</a>
        </span>
      </label>

      <button class="auth-panel__submit" type="submit" :disabled="registerLoading">
        <span v-if="registerLoading" class="auth-spinner" />
        {{ registerLoading ? '...' : $t('authInviteSubmit') }}
      </button>

      <p class="auth-panel__footer">
        {{ $t('authHasAccount') }}
        <router-link to="/login" class="auth-panel__footer-link">{{ $t('authTabLogin') }}</router-link>
      </p>
    </form>
  </AuthLayout>
</template>

<script>
import axios from 'axios';
import { mapActions } from 'vuex';
import { createToaster } from '@meforma/vue-toaster';
import useVuelidate from '@vuelidate/core';
import { required, minLength, helpers } from '@vuelidate/validators';
import AuthLayout from '@/components/auth/AuthLayout.vue';
import AuthInput from '@/components/auth/AuthInput.vue';
import { AUTH_DOCS } from '@/constants/authDocs';

const toaster = createToaster({ position: 'top-right', duration: 2000 });

function queryString(value) {
  if (Array.isArray(value)) return value[0] ?? '';
  return typeof value === 'string' ? value : '';
}

export default {
  name: 'RegistrationPage',
  components: { AuthLayout, AuthInput },
  data() {
    return {
      v$: useVuelidate(),
      registerLoading: false,
      password: '',
      confirmPassword: '',
      agree: false,
      DOCS: { ...AUTH_DOCS },
    };
  },
  validations() {
    return {
      password: {
        required: helpers.withMessage(this.$t('req'), required),
        minLength: helpers.withMessage(this.$t('minLength'), minLength(6)),
      },
      confirmPassword: {
        required: helpers.withMessage(this.$t('req'), required),
        sameAsPassword: helpers.withMessage(this.$t('errPass'), (value) => value === this.password),
      },
    };
  },
  computed: {
    inviteToken() {
      return queryString(this.$route.query.token);
    },
    inviteEmail() {
      const raw = queryString(this.$route.query.email);
      if (!raw) return '';
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    },
    hasValidLink() {
      return Boolean(this.inviteToken);
    },
    layoutTitle() {
      return this.hasValidLink ? this.$t('authInviteTitle') : this.$t('authInviteInvalidTitle');
    },
    layoutSubtitle() {
      if (!this.hasValidLink) return '';
      return this.inviteEmail
        ? this.$t('authInviteSub')
        : this.$t('authInviteSubNoEmail');
    },
    passwordErrors() {
      if (!this.v$.password.$dirty && !this.v$.password.$error) return [];
      return this.v$.password.$errors.map((e) => e.$message);
    },
    confirmPasswordErrors() {
      if (!this.v$.confirmPassword.$dirty && !this.v$.confirmPassword.$error) return [];
      return this.v$.confirmPassword.$errors.map((e) => e.$message);
    },
  },
  methods: {
    ...mapActions(['requestUser']),
    async register() {
      await this.v$.$validate();

      if (!this.agree) {
        toaster.error(
          'Подтвердите ваше согласие с правилами пользования и политикой конфиденциальности.'
        );
        return;
      }

      if (this.v$.$invalid) {
        toaster.error(this.$t('err'));
        return;
      }

      this.registerLoading = true;
      try {
        const res = await axios.post('/register-by-link', {
          token: this.inviteToken,
          password: this.password,
          password_confirmation: this.confirmPassword,
        });

        if (res.status === 200) {
          localStorage.setItem('access_token', res.data.token);
          await this.requestUser();
          toaster.success(this.$t('loginSuccess'));
          this.redirectAfterLogin();
        }
      } catch (err) {
        if (err.response?.data?.errors?.token) {
          toaster.error(err.response.data.errors.token[0]);
        } else if (err.response?.data?.errors?.password) {
          toaster.error(err.response.data.errors.password[0]);
        } else {
          toaster.error(err?.response?.data?.message ?? this.$t('err'));
          console.error(err);
        }
      } finally {
        this.registerLoading = false;
      }
    },
    redirectAfterLogin() {
      const role = this.$store.state.role;

      if (role === 'user') {
        const home = this.$store.state.hasApprovedPrograms ? '/platform' : '/programs';
        this.$router.push(home);
      } else if (role === 'guest') {
        this.$router.push('/platform');
      } else if (role === 'curator' || role === 'expert') {
        this.$router.push('/curator');
      } else if (role === 'supervisor' || role === 'chief') {
        this.$router.push('/supervisor');
      } else {
        this.$router.push('/');
      }
    },
    toMain() {
      this.$router.push('/');
    },
  },
};
</script>
